/**
 * Hook: useStreamingResponse
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R05
 *
 * Custom hook for consuming SSE streaming responses from the chat API.
 * Provides incremental content updates as the LLM generates tokens.
 *
 * FRONTEND-WEB-V1-R05: Render streaming LLM responses incrementally.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Citation from grounding results — passthrough of LLMSemanticSearch fields.
 * GALEN-CHAT-V1-R03 (Bug #3858)
 * BROWSE-V1-R20: GuidelineId + GuidelineSectionId carry the upstream
 * guideline identifiers used by the chat citation card to render the
 * cross-module `<BrowseLink>` (mirrors what search result cards do).
 */
interface StreamCitation {
  id: string;
  GuidelineId?: number | null;
  GuidelineSectionId?: number | null;
  ShortTitle?: string | null;
  GuidelineSectionName?: string | null;
  RecommendationText?: string | null;
  ExplanatoryText?: string | null;
  IndicationText?: string | null;
  RecommendationStrength?: string | null;
  EvidenceStrength?: string | null;
  SectionText?: string | null;
  SummaryVersionContent?: string | null;
  PageReference?: string | null;
}

/** Raw citation from SSE JSON (fields may be null). */
interface RawCitation {
  id: string;
  GuidelineId?: number | null;
  GuidelineSectionId?: number | null;
  ShortTitle?: string | null;
  GuidelineSectionName?: string | null;
  RecommendationText?: string | null;
  ExplanatoryText?: string | null;
  IndicationText?: string | null;
  RecommendationStrength?: string | null;
  EvidenceStrength?: string | null;
  SectionText?: string | null;
  SummaryVersionContent?: string | null;
  PageReference?: string | null;
}

/** SSE event types from the backend streaming endpoint. */
interface StreamEvent {
  event: 'start' | 'chunk' | 'done' | 'error';
  content?: string | null;
  cumulative?: string | null;
  done: boolean;
  conversation_id?: string;
  /**
   * Assistant message UUID for feedback linkage, emitted on the done event.
   * Mirrors ChatResponse.message_id so stream and non-stream paths expose
   * the same feedback contract (GALEN-CHAT-V1-R12, Bug #4332).
   */
  message_id?: string | null;
  safety_decision?: 'allow' | 'block' | 'clarify';
  /** GALEN-CHAT-V1-R03: Citations included in the done event. */
  citations?: RawCitation[];
  /** GALEN-CHAT-V1-R03 (Bug #3861): Whether the response is grounded. */
  is_grounded?: boolean;
  error?: string;
}

/** State returned by the hook. */
export interface StreamingState {
  /** Accumulated response content so far. */
  content: string;
  /** Whether a stream is currently active. */
  isStreaming: boolean;
  /** Error message if the stream failed. */
  error: string | null;
  /** Conversation ID returned by the backend. */
  conversationId: string | null;
  /**
   * Assistant message UUID from the backend done event, used for
   * /api/feedback linkage. Null until the done event arrives.
   * GALEN-CHAT-V1-R12 (Bug #4332).
   */
  messageId: string | null;
  /** Safety decision from the backend. */
  safetyDecision: 'allow' | 'block' | 'clarify' | null;
  /** GALEN-CHAT-V1-R03: Citations from grounding results. */
  citations: StreamCitation[];
  /** GALEN-CHAT-V1-R03 (Bug #3861): Whether the response is grounded. */
  isGrounded: boolean;
}

/** Request body for the streaming chat endpoint. */
interface StreamChatRequest {
  query: string;
  use_grounding: boolean;
  /**
   * Backend conversation UUID to continue. When absent, the backend creates
   * a new conversation row. GALEN-CHAT-V1-R09 (Bug #4316): multi-turn
   * context is now loaded from the DB keyed by conversation_id — the
   * obsolete conversation_history request field was dropped (backend
   * already ignored it via extra="ignore" per Bug #4009).
   */
  conversation_id?: string | null;
}

/**
 * Parse SSE lines from a text chunk.
 *
 * SSE format: `data: {...}\n\n`
 * May receive partial lines across chunks, so we buffer.
 */
function parseSSELines(text: string): StreamEvent[] {
  const events: StreamEvent[] = [];
  const lines = text.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('data: ')) {
      try {
        const json = trimmed.slice(6);
        const parsed = JSON.parse(json) as StreamEvent;
        events.push(parsed);
      } catch {
        // Ignore malformed lines
      }
    }
  }

  return events;
}

/**
 * Hook for consuming streaming chat responses via SSE.
 *
 * FRONTEND-WEB-V1-R05: Render streaming LLM responses incrementally.
 *
 * @returns Streaming state and a function to initiate streaming.
 */
export function useStreamingResponse(): [
  StreamingState,
  (request: StreamChatRequest) => Promise<void>,
  () => void,
] {
  const [state, setState] = useState<StreamingState>({
    content: '',
    isStreaming: false,
    error: null,
    conversationId: null,
    messageId: null,
    safetyDecision: null,
    citations: [],
    isGrounded: true,
  });

  const abortRef = useRef<AbortController | null>(null);

  // Ref-based content accumulation to avoid React 18 batching issues.
  // Content is written to the ref immediately per SSE event, then
  // flushed to React state via requestAnimationFrame (~60fps).
  const contentRef = useRef('');
  const rafIdRef = useRef<number>(0);

  // Clean up any pending rAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, []);

  /** Schedule a state update with the latest accumulated content. */
  const scheduleContentFlush = useCallback(() => {
    if (rafIdRef.current) return; // Already scheduled
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      setState((prev) => ({
        ...prev,
        content: contentRef.current,
      }));
    });
  }, []);

  /** Cancel any pending rAF and flush content immediately. */
  const flushContentNow = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    flushContentNow();
  }, [flushContentNow]);

  const startStream = useCallback(
    async (request: StreamChatRequest) => {
      // Cancel any existing stream
      cancel();

      const controller = new AbortController();
      abortRef.current = controller;

      contentRef.current = '';

      setState({
        content: '',
        isStreaming: true,
        error: null,
        conversationId: null,
        messageId: null,
        safetyDecision: null,
        citations: [],
        isGrounded: true,
      });

      try {
        const baseUrl =
          import.meta.env.VITE_API_URL || '/api';
        const token = localStorage.getItem('auth-token');

        const response = await fetch(`${baseUrl}/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('auth-token');
            localStorage.removeItem('galen-auth-storage');
            window.location.href = '/login';
            return;
          }
          throw new Error(
            `Server error (${response.status})`
          );
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Streaming not supported by browser');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        // Read the stream
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE events (split on double newline)
          const events = parseSSELines(buffer);

          // Keep only the unprocessed remainder
          const lastDoubleNewline = buffer.lastIndexOf('\n\n');
          if (lastDoubleNewline !== -1) {
            buffer = buffer.slice(lastDoubleNewline + 2);
          }

          for (const event of events) {
            if (event.event === 'start') {
              setState((prev) => ({
                ...prev,
                conversationId: event.conversation_id ?? null,
                safetyDecision: event.safety_decision ?? null,
              }));
            } else if (event.event === 'chunk' && event.cumulative) {
              // Write to ref immediately; schedule rAF flush
              contentRef.current = event.cumulative;
              scheduleContentFlush();
            } else if (event.event === 'done') {
              // Flush final content synchronously
              flushContentNow();
              // GALEN-CHAT-V1-R03: Capture citations from done event (Bug #3858 passthrough)
              const parsedCitations: StreamCitation[] = (event.citations ?? []).map(
                (c) => ({
                  id: c.id,
                  // BROWSE-V1-R20: Pass upstream guideline identifiers through
                  // so CitationDetail can render the cross-module BrowseLink.
                  // Previously dropped by the explicit whitelist below, which
                  // silently disabled "View in Browse" on every chat citation.
                  GuidelineId: c.GuidelineId ?? null,
                  GuidelineSectionId: c.GuidelineSectionId ?? null,
                  ShortTitle: c.ShortTitle ?? null,
                  GuidelineSectionName: c.GuidelineSectionName ?? null,
                  RecommendationText: c.RecommendationText ?? null,
                  ExplanatoryText: c.ExplanatoryText ?? null,
                  IndicationText: c.IndicationText ?? null,
                  RecommendationStrength: c.RecommendationStrength ?? null,
                  EvidenceStrength: c.EvidenceStrength ?? null,
                  SectionText: c.SectionText ?? null,
                  SummaryVersionContent: c.SummaryVersionContent ?? null,
                  PageReference: c.PageReference ?? null,
                })
              );
              setState((prev) => ({
                ...prev,
                content: event.cumulative || contentRef.current || prev.content,
                conversationId:
                  event.conversation_id ?? prev.conversationId,
                // GALEN-CHAT-V1-R12 (Bug #4332): Capture backend message_id
                // so feedback submission can link to the persisted assistant
                // row instead of a client-generated pseudo-id.
                messageId: event.message_id ?? prev.messageId,
                citations: parsedCitations.length > 0 ? parsedCitations : prev.citations,
                isGrounded: event.is_grounded !== false,
                isStreaming: false,
              }));
            } else if (event.event === 'error') {
              // AB#4380: event:error is now reserved exclusively for
              // transport / service failures. Safety block and clarify
              // outcomes are emitted by the backend as a normal start +
              // chunk + done sequence (allow/block/clarify are internal
              // LLM-flow concepts; the consumer sees a complete
              // pre-baked response with citations=[] and is_grounded=
              // false). The previous in-band branching on
              // event.safety_decision === 'block' || 'clarify' inside
              // event:error is no longer needed - the chunk/done
              // handlers above already populate content and
              // safetyDecision via event.cumulative on done and
              // event.safety_decision on the start event.
              flushContentNow();
              setState((prev) => ({
                ...prev,
                error: event.error || 'An unexpected error occurred',
                isStreaming: false,
              }));
            }
          }
        }

        // Ensure streaming is marked as done
        flushContentNow();
        setState((prev) => ({
          ...prev,
          content: contentRef.current || prev.content,
          isStreaming: false,
        }));
      } catch (err: unknown) {
        flushContentNow();
        if (err instanceof DOMException && err.name === 'AbortError') {
          // User cancelled - not an error
          setState((prev) => ({ ...prev, isStreaming: false }));
          return;
        }

        setState((prev) => ({
          ...prev,
          error:
            err instanceof Error
              ? err.message
              : 'An unexpected error occurred',
          isStreaming: false,
        }));
      } finally {
        abortRef.current = null;
      }
    },
    [cancel, scheduleContentFlush, flushContentNow]
  );

  return [state, startStream, cancel];
}
